import { useCallback, useMemo, useState } from "react"

import { BookingOrder, Criteria } from "models/bookingOrder"
import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { endOfDay, startOfDay } from "date-fns"
import {
  downloadCancelledBookingOrder,
  fetchBookingOrders,
  fetchBookingOrdersAggregations,
} from "api/bookingOrder"
import { queryCache, useQuery } from "react-query"

import { ReactComponent as BookingOrderIcon } from "assets/icon-b-os.svg"
import Button from "ui/buttons/Button"
import { COLUMNS } from "./columns"
import { CheckListItemLabel } from "components/inputs/CheckList/CheckListItemLabel"
import CreateBoModal from "components/Modals/CreateBoModal"
import { DateRangeButtonTrigger } from "components/inputs/DateRange"
import { EmptyState } from "components/EmptyState"
import { MediaPlan } from "models/mediaPlan"
import { Paginated } from "api/types"
import { Row } from "react-table"
import { Table } from "components/tables"
import { fetchPlans } from "api/plans"
import { history } from "my-history"
import { limitAndOffset, REDUCED_PAGE_SIZE } from "api/utils"
import { throttle } from "lodash"
import { useAuthenticated } from "providers/A12n"
import { useBOLifecycle } from "lifecycle/bookingOrder"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { useMPFilters } from "./utils"
import { useParams } from "react-router"
import { useSendFeedBack } from "components/FeedbackSnackbar"
import { useToggle } from "hooks/useToggle"
import { zonedTimeToUtc } from "date-fns-tz"
import BookingOrderCancellationModal from "../../../../components/Modals/BookingOrderCancellationModal"
import { downloadBlobFile } from "../../../../utils/files"

type Props = {
  name?: string
  campaignName?: string
  clientName?: string
  startDate?: string
  endDate?: string
}

const query_key = "plans"
function fetchOptions(_: string, name: string): Promise<Paginated<MediaPlan>> {
  const ps = new URLSearchParams([["limit", "200"]])
  if (name) {
    ps.set("name", name.trim())
  }
  return fetchPlans(query_key, ps.toString())
}

const fetchOptionsDebounced = throttle(fetchOptions, 200)

export default function BookingOrdersList(props: Props) {
  const { t } = useI18n()
  const ls = useLocationSearch()
  const { hasAuthority, dateFormatter, meta } = useAuthenticated()
  const { timeZone = "Etc/UTC" } = meta ?? {}
  const { id, boId } = useParams<{ id: string; boId: string }>()
  const { actions } = useBOLifecycle(boId)
  const { remove } = actions
  const { open, onOpen, onClose } = useToggle(false)
  const feedback = useSendFeedBack()
  const { name, campaignName, clientName, startDate, endDate } = props
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const [planInput, setPlanInput] = useState<string>("")
  const isModalOpen = Boolean(modalInfo)

  const [showCancellationModalInfo, setShowCancellationModalInfo] =
    useState(false)
  const [showCancellationModal, setShowCancellationModal] = useState(false)
  const [cancellationBoDetail, setCancellationBoDetail] = useState<{
    boId: string
  }>({ boId: "" })
  const [cancellationBoData, setCancellationBoData] = useState<{
    id: string
    name: string
  }>({ id: "", name: "" })

  const dateFrom = ls.get("startDate")
  const dateTo = ls.get("endDate")

  const params = ls.map((p) => limitAndOffset(p, REDUCED_PAGE_SIZE))

  !!id && params.set("mediaPlanIds", id)

  const { data, status } = useQuery(
    ["booking-orders", params.toString()],
    fetchBookingOrders,
  )

  const { data: aggregations, updatedAt: aggregationsUpdatedAt } = useQuery(
    ["campaignsAgs"],
    fetchBookingOrdersAggregations,
  )

  const mediaPlans = useQuery([query_key, planInput], fetchOptionsDebounced, {
    enabled: !id,
  })

  const filters = useMemo(() => {
    const vendorProductFilterOptions =
      aggregations?.channels
        ?.sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
        .map((vendorProduct) => ({
          value: vendorProduct.id,
          label: (
            <CheckListItemLabel secondary={""} primary={vendorProduct.name} />
          ),
          searchLabel: vendorProduct.name,
        })) || []

    const vendorFilterOptions =
      aggregations?.vendors
        ?.filter((v) => !v.intermediary)
        .sort((a, b) =>
          a.mediaOwner.name.trim().localeCompare(b.mediaOwner.name.trim()),
        )
        ?.map((vendor) => ({
          value: vendor.mediaOwner.id,
          label: (
            <CheckListItemLabel
              secondary={""}
              primary={vendor.mediaOwner.name}
            />
          ),
          searchLabel: vendor.mediaOwner.name,
        })) || []

    const clientFilterOptions =
      aggregations?.clients
        ?.sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
        .map((client) => ({
          value: client.id,
          label: <CheckListItemLabel secondary={""} primary={client.name} />,
          searchLabel: client.name,
        })) || []

    const mediaPlanFilterOptions =
      aggregations?.mediaPlans
        ?.sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
        .map((mediaPlan) => ({
          value: mediaPlan.id,
          label: <CheckListItemLabel secondary={""} primary={mediaPlan.name} />,
          searchLabel: mediaPlan.name,
        })) || []

    const commonOptions = [
      {
        name: "supplierProductId",
        label: "filters.vendorProduct",
        options: vendorProductFilterOptions,
      },
      {
        name: "mediaOwnerId",
        label: "filters.vendor",
        options: vendorFilterOptions,
      },
    ]

    const additionalOptions = [
      {
        name: "clientIds",
        label: "filters.client",
        options: clientFilterOptions,
      },
      {
        name: "mediaPlanIds",
        label: "filters.mediaPlan",
        options: mediaPlanFilterOptions,
      },
    ]

    const options = !!name
      ? commonOptions
      : commonOptions.concat(additionalOptions)

    return options
    //eslint-disable-next-line
  }, [aggregationsUpdatedAt])

  const onDateFilterReset = useCallback(() => {
    ls.set("startDate", "")
    ls.set("endDate", "")
  }, [ls])

  const onDateFilterChange = (s: Date | null, e: Date | null) => {
    s &&
      ls.set("startDate", zonedTimeToUtc(startOfDay(s), timeZone).toISOString())
    e && ls.set("endDate", zonedTimeToUtc(endOfDay(e), timeZone).toISOString())
  }

  const rows = data?.data ?? []

  const isLoading = status === "loading"

  const onDelete = useCallback(
    (rowId: string) => {
      setModalInfo({
        type: "primary_destructive",
        title: t("bo.modal.delete.title"),
        description: t("bo.modal.delete.description"),
        submitLabel: t("actions.delete"),
        onSubmit: async () =>
          await remove(rowId || "")
            .then(() => {
              setModalInfo(undefined)
              feedback(t("bo.delete.success"), "success")
              queryCache.invalidateQueries("booking-orders")
              queryCache.invalidateQueries(["plan", id, undefined])
              queryCache.invalidateQueries(["mediaPlan-filters", id])
            })
            .catch((e) => {
              setModalInfo(undefined)
              feedback(t(e.message) || t("bo.delete.error"), "error")
            }),
      })
    },
    [remove, feedback, id, t],
  )

  const onShowCancellationDetail = (boId: string) => {
    setCancellationBoDetail({
      boId: boId,
    })
    setShowCancellationModalInfo(true)
  }

  const onCancelBookingOrder = (id: string, name: string) => {
    setShowCancellationModal(true)
    setCancellationBoData({
      id: id,
      name: name,
    })
  }

  const onDownloadCancelledBo = (id: string, number: string | undefined) => {
    downloadCancelledBookingOrder(id)
      .then((response) => {
        downloadBlobFile(response, `${number ?? id}.xls`)
        feedback(t("bo.cancel.download.success"))
      })
      .catch(() => {
        feedback(t("bo.cancel.download.error"), "error")
      })
  }

  const onInputChange = (value: string) => {
    setPlanInput(value)
  }

  const canCreateBO = !!id
    ? hasAuthority(["AUTH_CREATE_BOOKING_ORDER"])
    : hasAuthority(["AUTH_CREATE_BOOKING_ORDER"]) && !!mediaPlans?.data?.total

  const { hasNoOptions: createBOHasNoOptions } = useMPFilters(!!id, id)

  return (
    <div style={{ justifyContent: "center", width: "100%" }}>
      <Table<BookingOrder>
        id="bo-list-table"
        columns={COLUMNS(
          t,
          dateFormatter,
          onShowCancellationDetail,
          onCancelBookingOrder,
          onDownloadCancelledBo,
          onDelete,
          id,
        )}
        data={rows ?? []}
        filters={filters}
        filterTitle={t("table.bookingOrders.filters.title")}
        customFilters={
          <DateRangeButtonTrigger
            onRangeChange={onDateFilterChange}
            onRangeClear={onDateFilterReset}
            start={dateFrom}
            end={dateTo}
            clearLabel={t("cancel")}
            placeholder={t("date.range.filter.placeholder")}
            hasStartAdornment
            asTextButton
          />
        }
        total={data?.total || 0}
        loading={isLoading}
        getRowProps={getBoRowProps}
        searchBy="name"
        actionButton={
          canCreateBO && (
            <Button
              color="primary"
              variant="contained"
              onClick={onOpen}
              disabled={!!id && createBOHasNoOptions}
            >
              {t("bo.create_button")}
            </Button>
          )
        }
        noResults={
          <EmptyState
            title={t("bookings.noresults.title")}
            icon={<BookingOrderIcon />}
          />
        }
        initialRowsPerPage={REDUCED_PAGE_SIZE}
      />
      <CreateBoModal
        planId={id}
        planName={name || ""}
        campaignName={campaignName}
        clientName={clientName}
        startDate={startDate}
        endDate={endDate}
        open={open}
        onClose={onClose}
        mediaPlans={mediaPlans?.data}
        onInputChange={onInputChange}
      />
      <ConfirmationModal
        onCancel={() => setModalInfo(undefined)}
        open={isModalOpen}
        {...modalInfo}
      />

      <BookingOrderCancellationModal
        readonlyMode
        open={showCancellationModalInfo}
        bookingOrderId={cancellationBoDetail?.boId ?? ""}
        onCloseReadOnlyModal={() => {
          setShowCancellationModalInfo(false)
        }}
      />

      <BookingOrderCancellationModal
        open={showCancellationModal}
        bookingOrderId={cancellationBoData?.id ?? ""}
        bookingOrderName={cancellationBoData?.name ?? ""}
        onSuccessCancellation={() => {
          setShowCancellationModal(false)
          queryCache.invalidateQueries("booking-orders")
          queryCache.invalidateQueries(["plan", id, undefined])
          queryCache.invalidateQueries(["mediaPlan-filters", id])
        }}
        onFailedCancellation={() => {
          setShowCancellationModal(false)
        }}
        onCloseCancellation={() => {
          setShowCancellationModal(false)
        }}
      />
    </div>
  )
}

function getBoRowProps(row: Row<BookingOrder & Criteria>) {
  const id = row.original?.id
  const mediaPlanId = row.original?.mediaPlanId
  return {
    onClick: () => history.push(`/booking-orders/${mediaPlanId}/${id}/table`),
  }
}
